/**
 * The parts of a proposal that carry money.
 *
 * These are edited as rows, never as prose. A proposal's investment table and
 * its payment schedules become a project's contract value and its billing
 * milestones the moment it is approved, so they have to stay data all the way
 * through — the alternative is a signed document whose numbers and whose
 * invoices came from two different places.
 *
 * Every figure shown here is resolved by `@dantria/core`, the same function the
 * API resolves with, so the editor, the client's copy and approval agree by
 * construction rather than by being kept in step.
 */
import { type ProposalSegment, itemAmount, resolveProposalSegment } from "@dantria/core";
import { fmtAmount, formatPercent, rateFromPercent, rateToPercent } from "../api";
import type { BrandPalette } from "../brand";
import { Button, Field, Td, Th, input } from "../ui";

export type Basis = "percent" | "amount";

export interface ItemDraft {
  key: number;
  itemNo: string;
  description: string;
  /**
   * The line's total, typed as the client should read it.
   *
   * There is no quantity or unit price on a proposal line. A client signs a
   * price for a piece of work, not a rate — "Kitchen cabinets, $33,375" — and
   * the measurements behind it belong to the estimate. Collecting a unit price
   * here also caused a real bug: the editor loaded the stored *total* into the
   * unit-price box beside the stored quantity, so any line with a quantity
   * above one was multiplied again on the next save.
   */
  amount: string;
  estimateLineId: string | null;
}

export interface ScheduleDraft {
  key: number;
  name: string;
  basis: Basis;
  /** Held as the operator types it — "50", not "0.5". Converted on save. */
  percent: string;
  amount: string;
  plannedDate: string;
}

export interface SegmentDraft {
  key: number;
  id: string | null;
  name: string;
  projectCode: string;
  retainagePercent: string;
  startDate: string;
  targetEndDate: string;
  /** Set once approved — the segment is locked to the project it made. */
  projectId: string | null;
  items: ItemDraft[];
  schedule: ScheduleDraft[];
}

let nextKey = 1;
export const newKey = () => nextKey++;

/**
 * A draft resolved through the same core function the server uses.
 *
 * Ids are synthesised from the row keys because a draft row has no id yet, and
 * `resolveProposalSegment` only uses them to key its output back to its input.
 */
export function resolveDraft(segment: SegmentDraft) {
  const shaped: ProposalSegment = {
    id: String(segment.key),
    seq: 1,
    name: segment.name,
    items: segment.items.map((i, idx) => ({
      id: String(i.key),
      seq: idx + 1,
      description: i.description,
      quantity: "1",
      amount: safeItemAmount(i.amount) ?? "0.00",
    })),
    schedule: segment.schedule.map((r, idx) => ({
      id: String(r.key),
      seq: idx + 1,
      name: r.name,
      basis: r.basis,
      percent: r.basis === "percent" ? rateFromPercent(r.percent || "0") : null,
      amount: r.basis === "amount" ? numeric(r.amount) : null,
    })),
  };
  try {
    return resolveProposalSegment(shaped);
  } catch {
    return null;
  }
}

/** A half-typed amount ("", "12.") must not blow up the running total the
 *  operator is watching while they type it. */
function numeric(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? v || "0" : "0";
}

/** A typed line total, tolerant of a half-typed row — returns `null` instead
 *  of throwing so the running total can show a dash until the field parses. */
export function safeItemAmount(amount: string): string | null {
  if (!amount.trim()) return null;
  try {
    return itemAmount(numeric(amount), "1");
  } catch {
    return null;
  }
}

export function emptySegment(name = ""): SegmentDraft {
  return {
    key: newKey(),
    id: null,
    name,
    projectCode: "",
    retainagePercent: "",
    startDate: "",
    targetEndDate: "",
    projectId: null,
    items: [
      {
        key: newKey(),
        itemNo: "01",
        description: "",
        amount: "",
        estimateLineId: null,
      },
    ],
    schedule: [
      {
        key: newKey(),
        name: "Deposit / contract signing",
        basis: "percent",
        percent: "50",
        amount: "",
        plannedDate: "",
      },
      {
        key: newKey(),
        name: "Delivery / installation",
        basis: "percent",
        percent: "40",
        amount: "",
        plannedDate: "",
      },
      {
        key: newKey(),
        name: "Final completion",
        basis: "percent",
        percent: "10",
        amount: "",
        plannedDate: "",
      },
    ],
  };
}

const cell = `${input} py-1`;

/**
 * One project-to-be: what the client is buying, and when they pay for it.
 *
 * A segment that has already become a project keeps its name and schedule
 * editable but cannot be removed — the project, its milestones and its
 * commitments all point back at it.
 */
export function SegmentEditor({
  segment,
  currency,
  onChange,
  onRemove,
  removable,
}: {
  segment: SegmentDraft;
  currency: string;
  onChange: (next: SegmentDraft) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  const resolved = resolveDraft(segment);
  const set = (patch: Partial<SegmentDraft>) => onChange({ ...segment, ...patch });

  const setItem = (key: number, patch: Partial<ItemDraft>) =>
    set({ items: segment.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) });
  const setRow = (key: number, patch: Partial<ScheduleDraft>) =>
    set({ schedule: segment.schedule.map((r) => (r.key === key ? { ...r, ...patch } : r)) });

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <Field label="Part of the job" hint="Becomes the project's name">
            <input
              className={input}
              value={segment.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Kitchen"
            />
          </Field>
        </div>
        <div className="w-32">
          <Field label="Project code" hint={segment.projectId ? "" : "Blank auto-numbers"}>
            <input
              className={input}
              value={segment.projectCode}
              onChange={(e) => set({ projectCode: e.target.value })}
              disabled={!!segment.projectId}
            />
          </Field>
        </div>
        <div className="w-36">
          <Field label="Starts">
            <input
              type="date"
              className={input}
              value={segment.startDate}
              onChange={(e) => set({ startDate: e.target.value })}
            />
          </Field>
        </div>
        <div className="w-36">
          <Field label="Target end">
            <input
              type="date"
              className={input}
              value={segment.targetEndDate}
              onChange={(e) => set({ targetEndDate: e.target.value })}
            />
          </Field>
        </div>
        {removable ? (
          <Button variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        ) : (
          segment.projectId && (
            <span className="pb-2 text-xs text-emerald-700">Live project — can't be removed</span>
          )
        )}
      </div>

      {/* ---- investment lines ---- */}
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Investment
      </div>
      <table className="mb-2 w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
            <Th>Item</Th>
            <Th>Description</Th>
            <Th right>Total</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {segment.items.map((item) => {
            return (
              <tr key={item.key} className="border-b border-neutral-100">
                <Td>
                  <input
                    className={`${cell} w-14`}
                    value={item.itemNo}
                    onChange={(e) => setItem(item.key, { itemNo: e.target.value })}
                  />
                </Td>
                <Td>
                  <input
                    className={cell}
                    value={item.description}
                    onChange={(e) => setItem(item.key, { description: e.target.value })}
                    placeholder="What this line covers"
                  />
                </Td>
                <Td right>
                  <input
                    className={`${cell} w-32 text-right tabular`}
                    value={item.amount}
                    onChange={(e) => setItem(item.key, { amount: e.target.value })}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                </Td>
                <Td right>
                  <Button
                    variant="ghost"
                    onClick={() => set({ items: segment.items.filter((i) => i.key !== item.key) })}
                  >
                    ×
                  </Button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mb-4 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() =>
            set({
              items: [
                ...segment.items,
                {
                  key: newKey(),
                  itemNo: String(segment.items.length + 1).padStart(2, "0"),
                  description: "",
                  amount: "",
                  estimateLineId: null,
                },
              ],
            })
          }
        >
          + Add line
        </Button>
        <div className="text-sm">
          <span className="text-neutral-500">Part total </span>
          <span className="tabular font-semibold">
            {fmtAmount(resolved?.total ?? "0.00", currency)}
          </span>
        </div>
      </div>

      {/* ---- payment schedule ---- */}
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Payment schedule
      </div>
      <table className="mb-2 w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
            <Th>Milestone</Th>
            <Th>Basis</Th>
            <Th right>Percent / amount</Th>
            <Th right>Works out to</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {segment.schedule.map((row) => {
            const amount = resolved?.rows.find((r) => r.id === String(row.key))?.amount;
            return (
              <tr key={row.key} className="border-b border-neutral-100">
                <Td>
                  <input
                    className={cell}
                    value={row.name}
                    onChange={(e) => setRow(row.key, { name: e.target.value })}
                  />
                </Td>
                <Td>
                  <select
                    className={`${cell} w-28`}
                    value={row.basis}
                    onChange={(e) => setRow(row.key, { basis: e.target.value as Basis })}
                  >
                    <option value="percent">Percent</option>
                    <option value="amount">Fixed</option>
                  </select>
                </Td>
                <Td right>
                  {row.basis === "percent" ? (
                    <div className="flex items-center justify-end gap-1">
                      <input
                        className={`${cell} w-20 text-right tabular`}
                        value={row.percent}
                        onChange={(e) => setRow(row.key, { percent: e.target.value })}
                        inputMode="decimal"
                      />
                      <span className="text-neutral-400">%</span>
                    </div>
                  ) : (
                    <input
                      className={`${cell} w-28 text-right tabular`}
                      value={row.amount}
                      onChange={(e) => setRow(row.key, { amount: e.target.value })}
                      inputMode="decimal"
                      placeholder="0.00"
                    />
                  )}
                </Td>
                <Td right mono muted>
                  {amount ? fmtAmount(amount, currency) : "—"}
                </Td>
                <Td right>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      set({ schedule: segment.schedule.filter((r) => r.key !== row.key) })
                    }
                  >
                    ×
                  </Button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() =>
            set({
              schedule: [
                ...segment.schedule,
                {
                  key: newKey(),
                  name: "",
                  basis: "percent",
                  percent: "",
                  amount: "",
                  plannedDate: "",
                },
              ],
            })
          }
        >
          + Add milestone
        </Button>
        {/* The one number worth interrupting for: a schedule that doesn't add
            up to what was quoted bills less than the client agreed to pay. */}
        {resolved && resolved.unscheduled !== "0.00" && segment.schedule.length > 0 && (
          <span className="text-sm text-amber-700">
            {fmtAmount(resolved.unscheduled, currency)} of this part isn't in the schedule
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * The client's copy
 * ---------------------------------------------------------------- */

/**
 * Colors on the client's copy are inline `style`, never a utility class.
 * They come from the tenant at runtime, so there is no class for Tailwind to
 * generate; and the PDF export rasterizes this DOM (see `../pdf.ts`) with a
 * renderer that understands plain hex and little else.
 */
export interface InvestmentRow {
  itemNo: string;
  /** The part's name stands in for a line nobody described, so a one-line
   *  segment still reads as something rather than as a bare amount. */
  description: string;
  amount: string;
}

/**
 * The investment table's rows, flattened across parts, and the total they foot
 * to.
 *
 * Separated from the component because the Word export (`../docx.ts`) builds
 * the same table out of the same rows and cannot reach into JSX for them. Each
 * line is a total, and nothing here invites the client to check a rate — the
 * measurements behind it belong to the estimate.
 */
export function investmentRows(segments: readonly SegmentDraft[]): {
  rows: InvestmentRow[];
  total: string;
} {
  const rows = segments.flatMap((seg) =>
    seg.items.map((item) => ({
      itemNo: item.itemNo,
      description: item.description || seg.name,
      amount: safeItemAmount(item.amount) ?? "0.00",
    })),
  );
  return { rows, total: rows.reduce((acc, r) => acc + Number(r.amount), 0).toFixed(2) };
}

/** A blank slot, not a line — the signature grid leaves the cell beside each
 *  signature empty so the two columns stay in step down the block. */
export type AcceptanceCell = { label: string; value: string } | null;

/** The signature block's eight slots, in reading order across two columns.
 *  Which are blank is part of the layout, so — like the investment rows — it
 *  is decided once and drawn twice. */
export function acceptanceCells(
  accepted: { acceptedByName?: string | null | undefined; acceptedAt?: string | null | undefined },
  companyName: string,
): AcceptanceCell[] {
  const line = (label: string, value?: string | null): AcceptanceCell => ({
    label,
    value: value ?? "",
  });
  return [
    line("Client name", accepted.acceptedByName),
    line("Date", accepted.acceptedAt ? accepted.acceptedAt.slice(0, 10) : null),
    line("Signature"),
    null,
    line(companyName ? `For ${companyName}` : "Company representative"),
    line("Date"),
    line("Signature"),
    null,
  ];
}

export function InvestmentTable({
  rows,
  total,
  currency,
  palette,
}: {
  rows: readonly InvestmentRow[];
  total: string;
  currency: string;
  palette: BrandPalette;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr
          className="text-left text-xs font-semibold uppercase tracking-wide"
          style={{ backgroundColor: palette.base, color: palette.onBase }}
        >
          <th className="px-3 py-2">Item</th>
          <th className="px-3 py-2">Description</th>
          <th className="px-3 py-2 text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={`${row.description}-${i}`} className="border-b border-neutral-200">
            <td className="px-3 py-2.5 tabular text-neutral-500">{row.itemNo}</td>
            <td className="px-3 py-2.5">{row.description}</td>
            <td className="px-3 py-2.5 text-right tabular">{fmtAmount(row.amount, currency)}</td>
          </tr>
        ))}
        <tr style={{ backgroundColor: palette.wash }}>
          <td className="px-3 py-2.5" colSpan={2} />
          <td
            className="px-3 py-2.5 text-right tabular font-semibold"
            style={{ color: palette.ink }}
          >
            {fmtAmount(total, currency)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function ScheduleTable({
  rows,
  currency,
  palette,
}: {
  rows: readonly { id: string; name: string; amount: string; share: string }[];
  currency: string;
  palette: BrandPalette;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr
          className="text-left text-xs font-semibold uppercase tracking-wide"
          style={{ backgroundColor: palette.base, color: palette.onBase }}
        >
          <th className="px-3 py-2">Milestone</th>
          <th className="px-3 py-2 text-right">Percentage</th>
          <th className="px-3 py-2 text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-b border-neutral-200">
            <td className="px-3 py-2.5">{row.name}</td>
            <td className="px-3 py-2.5 text-right tabular text-neutral-500">
              {formatPercent(row.share)}
            </td>
            <td className="px-3 py-2.5 text-right tabular">{fmtAmount(row.amount, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AcceptanceBlock({
  acceptedByName,
  acceptedAt,
  companyName,
}: {
  acceptedByName?: string | null;
  acceptedAt?: string | null;
  /** The registered entity, so the signature line names who is actually bound. */
  companyName?: string | undefined;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-5">
      {acceptanceCells({ acceptedByName, acceptedAt }, companyName ?? "").map((cell, i) =>
        cell ? (
          <div key={`${cell.label}-${i}`} className="border-b border-neutral-300 pb-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
              {cell.label}
            </div>
            {/* A non-breaking space, not an empty string: an unsigned slot has
                to keep its height or the rule under it rides up into the label. */}
            <div className="min-h-6 text-sm text-neutral-800">{cell.value || " "}</div>
          </div>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: a blank slot has nothing else to key on
          <div key={`gap-${i}`} />
        ),
      )}
    </div>
  );
}

export { rateToPercent };
